import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "../../../../shared/ui/cn";
import type { ReportSubjectKind } from "../../domain/PractitionerReport";

export type SubjectTypeOption = "all" | ReportSubjectKind;

const OPTIONS: SubjectTypeOption[] = ["all", "patient", "couple", "group"];

const LABEL: Record<SubjectTypeOption, string> = {
    all: "reports.table.all",
    patient: "reports.type.individual",
    couple: "reports.type.couple",
    group: "reports.type.group",
};

interface Props {
    value: SubjectTypeOption;
    onChange: (value: SubjectTypeOption) => void;
    /** How many rows each option would show, beside its label. */
    counts: Record<SubjectTypeOption, number>;
    /** The kinds the centre runs; a kind switched off is not offered. All by default. */
    kinds?: ReportSubjectKind[];
}

/**
 * Everybody, or only individual patients, couples or groups. A radio group
 * drawn as a segmented control, moved through with the arrow keys.
 */
export default function SubjectTypeFilter({ value, onChange, counts, kinds }: Props) {
    const { t } = useTranslation();
    const options = OPTIONS.filter((option) => option === "all" || !kinds || kinds.includes(option));
    const refs = useRef<Record<string, HTMLButtonElement | null>>({});

    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        const offset =
            event.key === "ArrowRight" || event.key === "ArrowDown"
                ? 1
                : event.key === "ArrowLeft" || event.key === "ArrowUp"
                  ? -1
                  : 0;
        if (offset === 0) return;
        event.preventDefault();
        const next = options[(index + offset + options.length) % options.length];
        onChange(next);
        refs.current[next]?.focus();
    };

    return (
        <div
            role="radiogroup"
            aria-label={t("reports.table.filterLabel")}
            className="inline-flex flex-wrap gap-1 rounded-lg border border-gray-300 bg-white p-1 dark:border-gray-700 dark:bg-gray-900"
        >
            {options.map((option, index) => {
                const selected = option === value;
                return (
                    <button
                        key={option}
                        ref={(el) => {
                            refs.current[option] = el;
                        }}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onChange(option)}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                        className={cn(
                            "min-h-[36px] rounded-md px-3 text-sm font-medium transition-colors",
                            selected
                                ? "bg-brand-500 text-white shadow-theme-xs"
                                : "text-content-secondary hover:bg-gray-100 hover:text-content-primary dark:hover:bg-white/5"
                        )}
                    >
                        {t(LABEL[option])}
                        <span className={cn("ml-1.5 tabular-nums", selected ? "text-white/80" : "text-content-tertiary")}>
                            {counts[option]}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
